import type {
	IContentfulAdapter,
	IContentfulError,
	ILegalContentConstructorParams,
	IWelcomeCarouselSlideConstructorParams,
} from "@services/sdk/interfaces";
import type { ContentfulClientApi, EntryCollection } from "contentful";

import {
	type ArrayWelcomeCarouselSlideResult,
	Asset,
	type AssetResult,
	type ErrorResponse,
	LegalContent,
	type LegalContentResult,
	Result,
	WelcomeCarouselSlide,
} from "@services/sdk/models";

import { Actioner } from "@services/actioner";
import ErrorResponseFactory from "@services/sdk/models/error-response-factory";

import ImpactRange, {
	type IImpactRangeConstructorParams,
} from "@services/sdk/models/impact-range";

import FAQPage from "@services/sdk/models/faq-page";
import ImpactPage, {
	type CombinedPageEntry,
} from "@services/sdk/models/impact-page";

export type WelcomeCarouselSlidesResponse =
	EntryCollection<IWelcomeCarouselSlideConstructorParams>;

export type LegalContentResponse =
	EntryCollection<ILegalContentConstructorParams>;

export const createContentfulAdapter = (
	contentfulClient: ContentfulClientApi,
): IContentfulAdapter => {
	return {
		getWelcomeCarouselSlides:
			async (): Promise<ArrayWelcomeCarouselSlideResult> => {
				try {
					const response: WelcomeCarouselSlidesResponse =
						await contentfulClient.getEntries({
							content_type: "welcomeScreen",
						});

					const data: Array<IWelcomeCarouselSlideConstructorParams> =
						response.items.map(
							(item): IWelcomeCarouselSlideConstructorParams => item.fields,
						);
					return Result.Success(WelcomeCarouselSlide.ConstructList(data));
				} catch (err: any) {
					Actioner.trigger(Actioner.keyDefs.ERROR, err);
					return Result.Failure(
						ErrorResponseFactory.ErrorGetWelcomeCarouselSlides(err),
					);
				}
			},
		getLegalContent: async (key: string): Promise<LegalContentResult> => {
			try {
				const response: LegalContentResponse =
					await contentfulClient.getEntries<ILegalContentConstructorParams>({
						content_type: "legalContent",
						"fields.key": key,
					});

				if (response.items.length > 0) {
					return Result.Success(
						LegalContent.Construct(response.items[0].fields),
					);
				}

				const syntheticContentfulError: IContentfulError = {
					message: `Could not find legal content for key: ${key}`,
				};

				Actioner.trigger(
					Actioner.keyDefs.ERROR,
					new Error("unable to get legal content from contentful"),
				);
				return Result.Failure(
					ErrorResponseFactory.ErrorGetLegalContent(syntheticContentfulError),
				);
			} catch (err: any) {
				Actioner.trigger(Actioner.keyDefs.ERROR, err);
				return Result.Failure(ErrorResponseFactory.ErrorGetLegalContent(err));
			}
		},
		getAsset: async (id: string): Promise<AssetResult> => {
			try {
				const response = await contentfulClient.getAsset(id);
				if (response.fields?.file) {
					return Result.Success(Asset.Construct(response.fields.file));
				}

				const syntheticContentfulError: IContentfulError = {
					message: `Could not find asset for id: ${id}`,
				};

				Actioner.trigger(
					Actioner.keyDefs.ERROR,
					new Error("unable to get asset from contentful"),
				);
				return Result.Failure(
					ErrorResponseFactory.ErrorGetAsset(syntheticContentfulError),
				);
			} catch (err: any) {
				Actioner.trigger(Actioner.keyDefs.ERROR, err);
				return Result.Failure(ErrorResponseFactory.ErrorGetAsset(err));
			}
		},
		getImpactRanges: async (): Promise<
			Result<Array<ImpactRange>, ErrorResponse>
		> => {
			try {
				const response = await contentfulClient.getEntries({
					content_type: "appModuleImpactRange",
				});

				// @ts-ignore
				const data = response.items.map(
					(item: any) => item.fields as IImpactRangeConstructorParams,
				);

				return Result.Success(ImpactRange.ConstructList(data));
			} catch (err: any) {
				Actioner.trigger(Actioner.keyDefs.ERROR, err);
				return Result.Failure(ErrorResponseFactory.ErrorGetImpactRanges(err));
			}
		},
		getImpactPage: async (): Promise<Result<ImpactPage, ErrorResponse>> => {
			try {
				// @ts-ignore
				const response = await contentfulClient.getEntry(env.impact.pageId, {
					include: 3,
				});

				return Result.Success(
					ImpactPage.Construct(<CombinedPageEntry>response),
				);
			} catch (err: any) {
				Actioner.trigger(Actioner.keyDefs.ERROR, err);
				return Result.Failure(ErrorResponseFactory.ErrorGetImpactPage(err));
			}
		},
		getFAQPage: async (): Promise<Result<FAQPage, ErrorResponse>> => {
			try {
				// @ts-ignore
				const response = await contentfulClient.getEntry(env.impact.FAQpageId, {
					include: 3,
				});

				return Result.Success(FAQPage.Construct(<CombinedPageEntry>response));
			} catch (err: any) {
				Actioner.trigger(Actioner.keyDefs.ERROR, err);
				return Result.Failure(ErrorResponseFactory.ErrorGetFAQPage(err));
			}
		},
	};
};
